import * as React from 'react'
import {
  CompactSelection,
  CustomRenderer,
  DataEditor,
  DataEditorRef,
  GridColumn,
  GridSelection,
  Item,
} from '@basehub/glide-data-grid'
import { useInfiniteQuery } from '@tanstack/react-query'
import { DEFAULT_COLUMNS, useGetEventCellContent } from './use-get-cell-content'
import { RENDERERS } from '../list/views/table/components'
import {
  HEADER_HEIGHT_WITH_BORDERS,
  LIST_BLOCK_DARK_THEME,
  LIST_BLOCK_LIGHT_THEME,
  ROW_HEIGHT,
} from '../list/views/table/theme'
import { useTheme } from 'next-themes'
import { FullBleedContainer } from '../../../../_components/full-bleed-container'
import type { EventBlock, EventField } from '@basehub/data/replicache/index'
import { BlockVariant } from '..'
import { ArrowDownIcon } from '@radix-ui/react-icons'
import { Button } from '~/sotano-kit/primitives/button'
import { useEditorStore } from '../../editor-store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { clsx } from 'clsx'
import debounce from 'lodash.debounce'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useTabsStore, useGetTabPrefix } from '../../tabs/store'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import Link from 'next/link'
import { Spinner2 } from '~/components/misc/spinner'
import { useEditorMode } from '../../editor-mode-provider'
import { TableContextMenu } from './context-menu'
import { useProtectedFileToken } from '~/hooks-data/event/use-protected-file-token'
import { analyticsEvents } from '@basehub/data/db/schema'

const STEP = 6
export const getEventTableQueryKey = ({
  blockId,
  teamId,
  repoId,
}: {
  blockId: string
  teamId?: string
  repoId?: string
}) => {
  return ['event-block', blockId, teamId, repoId] as const
}

export const Table = ({
  block,
  variant,
  path,
}: {
  block: EventBlock
  variant?: BlockVariant
  path: string
}) => {
  const tabPrefix = useGetTabPrefix()
  const editorMode = useEditorMode()
  const isOverriding =
    editorMode === 'write:value-only' || editorMode === 'write:value-and-title'
  const readOnly = editorMode !== 'write:all' && !isOverriding
  const replicacheData = useRepoReplicache().data
  const repoId = replicacheData?.repoId
  const teamId = replicacheData?.team.id
  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )
  const isHovered = useEditorStore(
    (s) => s.hoveredBlockId === idToSourceBlockId(block)
  )

  const {
    data,
    isInitialLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery(
    getEventTableQueryKey({
      blockId: idToSourceBlockId(block),
      teamId: teamId,
      repoId: repoId,
    }),
    async ({ queryKey, pageParam = 1 }) => {
      const skip = (pageParam - 1) * STEP
      const res = await fetch(
        `/api/v2/events?block-id=${queryKey[1]}&team-id=${queryKey[2]}&repo-id=${queryKey[3]}&first=${STEP}&skip=${skip}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )
      const data = (await res.json()) as
        | (typeof analyticsEvents.$inferSelect)[]
        | { error: string }
      if ('error' in data) {
        throw new Error(data.error)
      }

      return data.map((row) => ({
        ...row,
        value: row.value
          ? typeof row.value === 'string'
            ? JSON.parse(row.value)
            : row.value
          : null,
      }))
    },
    {
      enabled: Boolean(teamId && repoId),
      refetchInterval: 3 * 1000,
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length !== STEP ? undefined : allPages.length + 1,
      getPreviousPageParam: (_, allPages) =>
        allPages.length <= 1 ? undefined : allPages.length - 1,
    }
  )

  const enabledNextPage = Boolean(
    hasNextPage && !isFetchingNextPage && !isInitialLoading
  )

  const schemaColumns = React.useMemo(
    () =>
      Object.values({
        ...block.constraints.schema,
        ...block.value?.schema,
      }).filter((item): item is EventField => !!item),
    [block.constraints.schema, block.value?.schema]
  )

  const columns = React.useMemo(() => {
    const systemColumns = DEFAULT_COLUMNS.map((c) => ({
      ...c,
      order: block.typeOptions.systemColumnsOrder?.[c.name] ?? c.order,
    }))

    return (
      schemaColumns.length
        ? [...systemColumns, ...schemaColumns]
        : systemColumns
    ).sort((a, b) => a.order - b.order)
  }, [block.typeOptions.systemColumnsOrder, schemaColumns])

  const flattedResult = React.useMemo(
    () => data?.pages.flat() ?? [],
    [data?.pages]
  )

  const tokenQuery = useProtectedFileToken()

  const getCellContent = useGetEventCellContent({
    rows: flattedResult ?? [],
    columns,
    isInitialLoading,
    protectedFilesToken: tokenQuery.data?.assetsJwt,
  })

  const theme =
    useTheme().resolvedTheme === 'dark'
      ? LIST_BLOCK_DARK_THEME
      : LIST_BLOCK_LIGHT_THEME

  const headerOverrideText =
    useTheme().resolvedTheme === 'dark' ? '#909090' : '#646464'

  const bleedContainerRef = React.useRef<HTMLDivElement>(null)

  const tableHeight = React.useMemo(
    () =>
      !isInitialLoading
        ? HEADER_HEIGHT_WITH_BORDERS + flattedResult.length * ROW_HEIGHT
        : HEADER_HEIGHT_WITH_BORDERS + 6 * ROW_HEIGHT,
    [flattedResult.length, isInitialLoading]
  )
  const rowsNumber = React.useMemo(
    () => flattedResult.length || (isInitialLoading ? 6 : 0),
    [flattedResult.length, isInitialLoading]
  )

  const tableWidth = React.useMemo(() => {
    return columns.length > 4 ? undefined : '100%'
  }, [columns])

  const [gridSelection, setGridSelection] = React.useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty(),
  })

  // Clean highlighted cells when block goes out of focus
  React.useEffect(() => {
    const handleFocusChange = debounce(() => {
      if (contextMenuOn.current) return

      // editor portal
      const portal = document.querySelector<HTMLDivElement>('div#portal')

      const activeElementIsWithinEditingPortal = portal?.contains(
        document.activeElement
      )
      const activeElementIsWithinTable = bleedContainerRef.current?.contains(
        document.activeElement
      )
      const activeElementIsWithinContextMenu = contextMenuRef.current?.contains(
        document.activeElement
      )

      if (
        activeElementIsWithinTable ||
        activeElementIsWithinEditingPortal ||
        activeElementIsWithinContextMenu
      ) {
        return
      }

      setGridSelection({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
      })
    }, 80)

    window.addEventListener('focus', handleFocusChange, true)
    window.addEventListener('blur', handleFocusChange, true)

    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('focus', handleFocusChange, true)
      window.removeEventListener('blur', handleFocusChange, true)
    }
  }, [block.id])

  const replicache = replicacheData?.replicache
  const schemaRef = React.useRef({
    ...block.constraints.schema,
    ...block.value?.schema,
  })
  React.useEffect(() => {
    schemaRef.current = {
      ...block.constraints.schema,
      ...block.value?.schema,
    }
  }, [block.constraints.schema, block.value?.schema])
  const columnsRef = React.useRef(columns)
  React.useEffect(() => {
    columnsRef.current = columns
  }, [columns])

  const sourceBlockId = idToSourceBlockId(block)
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const undoManager = useRepoUndoManager()

  const focusOpTable = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          type: 'block:wrapper',
          blockId: sourceBlockId,
        },
        preferredTabKey: activeTabKey,
      },
    } satisfies RepoUndoOps[number]
  }, [sourceBlockId, activeTabKey])

  const handleColumnMoved = React.useCallback(
    async (from: number, to: number) => {
      if (!replicache?.mutate) return

      const columnToMove = columnsRef.current[from]
      if (!columnToMove) return

      const isMovingToRight = from < to
      const leftIndex = isMovingToRight ? to : to - 1
      const rightIndex = isMovingToRight ? to + 1 : to
      const columnLeft = columnsRef.current[leftIndex]
      const columnRight = columnsRef.current[rightIndex]

      let newOrder: number
      if (!columnLeft) {
        const firstColumnOrder = columnsRef.current[0]?.order
        newOrder = firstColumnOrder ? firstColumnOrder / 2 : 0
      } else if (!columnRight) {
        newOrder = columnLeft.order + 1
      } else {
        newOrder = (columnLeft.order + columnRight.order) / 2
      }

      let params: UpdateBlockParams | null = null
      if (columnToMove.name.startsWith('_sys_')) {
        if (isOverriding) {
          params = {
            path,
            id: block.id,
            instanceChildTarget: block.instanceChildTarget,
            data: {
              typeOptions: {
                systemColumnsOrder: {
                  [columnToMove.name]: newOrder,
                },
              },
            },
            strategy: 'merge-deep',
            isoNow: new Date().toISOString(),
          }
        }
      } else {
        const columnId = Object.entries(schemaRef.current).find(([_, v]) => {
          if (!v) return false
          return v.name === columnToMove.name
        })?.[0]
        if (!columnId) return

        params = {
          path,
          id: block.id,
          instanceChildTarget: block.instanceChildTarget,
          data: {
            ...(!isOverriding
              ? { constraints: { schema: { [columnId]: { order: newOrder } } } }
              : {
                  value: {
                    schema: {
                      [columnId]: block.value.schema?.[columnId]
                        ? { order: newOrder }
                        : { ...schemaRef.current[columnId], order: newOrder },
                    },
                  },
                }),
          },
          strategy: 'merge-deep',
          isoNow: new Date().toISOString(),
        }
      }

      if (!params) return

      const { undo } = await replicache.mutate.updateBlock(params)

      undoManager.current?.push({
        redo: [focusOpTable, { op: 'updateBlock', args: params }],
        undo: [
          focusOpTable,
          { op: 'blockBatchMutations', args: { mutations: undo } },
        ],
      })
    },
    [
      block.id,
      block.instanceChildTarget,
      block.value.schema,
      focusOpTable,
      isOverriding,
      path,
      replicache?.mutate,
      undoManager,
    ]
  )

  const dataEditorRef = React.useRef<DataEditorRef>(null)
  const contextMenuOn = React.useRef<Item | null>(null)
  const contextMenuRef = React.useRef<HTMLDivElement>(null)

  const LoadMoreButton = React.useCallback(() => {
    if (!hasNextPage || isInitialLoading) {
      return (
        <div
          className={clsx(
            'gap-x-1.5 flex items-center text-dark-faint font-medium mt-2 transition-opacity duration-300',
            !isSelected && !isHovered && 'invisible opacity-0'
          )}
        >
          <Spinner2 className="text-dark-accent size-4" />
          <span className="text-xs leading-normal">
            {isInitialLoading
              ? 'Retrieving events...'
              : "Start sending events. We're listening."}
          </span>
        </div>
      )
    }

    return (
      <Button
        onClick={(e) => {
          e.stopPropagation()
          fetchNextPage()
        }}
        onPointerDown={(e) => {
          e.stopPropagation()
          fetchNextPage()
        }}
        disabled={!enabledNextPage}
        unstyled
        focusStyled
        className={clsx(
          !isSelected && !isHovered && 'invisible opacity-0',
          'w-fit pr-4 mt-2 text-xs text-dark-faint hover:text-dark-muted transition-colors cursor-default flex items-center gap-x-0.5 disabled:text-dark-muted disabled:cursor-progress'
        )}
      >
        <ArrowDownIcon className="size-4" />
        Load more
      </Button>
    )
  }, [
    isInitialLoading,
    hasNextPage,
    enabledNextPage,
    isSelected,
    isHovered,
    fetchNextPage,
  ])

  const selectedRowIndexes = React.useMemo(() => {
    const _selectedRowIndexes = gridSelection.rows.toArray()
    if (!_selectedRowIndexes.length && gridSelection.current) {
      const start = gridSelection.current.range.y
      const height = gridSelection.current.range.height
      _selectedRowIndexes.push(
        ...Array.from({ length: height }, (_, i) => start + i)
      )
    }
    return _selectedRowIndexes
  }, [gridSelection])

  if (!schemaColumns.length) {
    return (
      <div className="border border-dark-border-solid bg-dark-base-dark rounded-lg flex flex-col items-center justify-center h-[277.5px] gap-y-2">
        <p className="text-sm font-medium text-dark-faint max-w-lg text-center">
          You don't have any schema fields to show. You can configure the event
          fields throughout the “Schema” section within the properties panel.
        </p>
        {!isOverriding && (
          <p className="text-sm font-medium text-dark-faint max-w-lg text-center">
            You can also{' '}
            <button
              className="underline hover:text-dark-muted transition-colors cursor-default"
              onClick={async () => {
                await replicache?.mutate.updateBlock({
                  path,
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: {
                    typeOptions: {
                      view: 'chart',
                    },
                  },
                  isoNow: new Date().toISOString(),
                  strategy: 'merge-deep',
                })
              }}
            >
              switch to chart view
            </button>{' '}
            to start visualizing incoming events without configuring the schema.
          </p>
        )}
      </div>
    )
  }

  return (
    <>
      <FullBleedContainer
        role="list-view-bleed-container"
        disabled={variant !== 'default'}
        ref={bleedContainerRef}
        maxWidth={1024}
        flexGrow
        bleedContainerClassName="px-20 min-h-[250px]"
      >
        {!isInitialLoading && flattedResult.length < 6 && (
          <div className="absolute top-0 left-0 right-0 bottom-0 border border-dark-border-solid bg-dark-base-dark rounded-lg flex items-center justify-center">
            {flattedResult.length === 0 &&
              (Boolean(teamId && repoId) ? (
                <span className="text-sm font-medium text-dark-faint max-w-lg text-center relative top-4">
                  Learn how to send an event in our{' '}
                  <Link
                    className="underline hover:text-dark-muted transition-colors cursor-default"
                    href="/docs/extras/forms"
                    target="_blank"
                  >
                    docs
                  </Link>
                  .
                </span>
              ) : (
                <span className="text-sm font-medium text-dark-faint max-w-lg text-center relative top-4">
                  Only team members can read events.
                  <br />
                  <Link
                    className="underline hover:text-dark-muted transition-colors cursor-default"
                    href="/docs/extras/forms"
                    target="_blank"
                  >
                    Learn how to use the Event block.
                  </Link>
                </span>
              ))}
          </div>
        )}
        <TableContextMenu
          ref={contextMenuRef}
          parentBlock={block}
          disabled={tabPrefix !== ''}
          selectedRows={
            selectedRowIndexes.length > 0
              ? selectedRowIndexes
                  .map((rowIndex) => flattedResult[rowIndex])
                  .filter(Boolean)
              : []
          }
        >
          <DataEditor
            ref={dataEditorRef}
            gridSelection={gridSelection}
            onGridSelectionChange={(newSelection) => {
              if (contextMenuOn.current) {
                contextMenuOn.current = null
                return
              }
              setGridSelection(newSelection)
            }}
            rowSelectionMode="multi"
            rowMarkers={
              readOnly
                ? 'number'
                : {
                    kind: 'both',
                    theme: { borderColor: 'transparent' },
                  }
            }
            width={tableWidth}
            rowHeight={ROW_HEIGHT}
            headerHeight={HEADER_HEIGHT_WITH_BORDERS}
            height={tableHeight}
            className={clsx(
              'basehub-glide-table border border-dark-border-solid rounded-t-lg animate-delayed-fade-in opacity-0',
              (flattedResult.length >= 6 || isInitialLoading) && 'rounded-b-lg'
            )}
            onCellContextMenu={(cell) => {
              // select cell if it was not selected
              if (
                !gridSelection.rows ||
                !selectedRowIndexes.includes(cell[1])
              ) {
                contextMenuOn.current = cell

                setGridSelection({
                  columns: CompactSelection.empty(),
                  rows: CompactSelection.fromSingleSelection(cell[1]),
                })
              }
            }}
            theme={theme}
            headerIcons={HEADER_ICONS}
            columns={columns.map((c) => {
              const isSystemColumn = c.name.startsWith('_sys_')

              return {
                width: isSystemColumn ? 154 : 180,
                grow: isSystemColumn ? 0 : 1,
                title: c.label ?? c.name,
                id: c.name,
                icon: isSystemColumn ? 'lock' : c.type,
                themeOverride: isSystemColumn
                  ? { textHeader: headerOverrideText }
                  : undefined,
              } satisfies GridColumn
            })}
            experimental={{
              scrollbarWidthOverride: 0,
            }}
            rows={rowsNumber}
            customRenderers={RENDERERS as unknown as CustomRenderer[]}
            getCellContent={getCellContent}
            onColumnMoved={readOnly ? undefined : handleColumnMoved}
            getCellsForSelection
          />
        </TableContextMenu>
      </FullBleedContainer>
      <LoadMoreButton />
    </>
  )
}

const HEADER_ICONS: Record<
  string,
  (props: { fgColor: string; bgColor: string }) => string
> = {
  // system
  lock: ({ bgColor }) => {
    return `<svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 4.63601C5 3.76031 5.24219 3.1054 5.64323 2.67357C6.03934 2.24705 6.64582 1.9783 7.5014 1.9783C8.35745 1.9783 8.96306 2.24652 9.35823 2.67208C9.75838 3.10299 10 3.75708 10 4.63325V5.99999H5V4.63601ZM4 5.99999V4.63601C4 3.58148 4.29339 2.65754 4.91049 1.99307C5.53252 1.32329 6.42675 0.978302 7.5014 0.978302C8.57583 0.978302 9.46952 1.32233 10.091 1.99162C10.7076 2.65557 11 3.57896 11 4.63325V5.99999H12C12.5523 5.99999 13 6.44771 13 6.99999V13C13 13.5523 12.5523 14 12 14H3C2.44772 14 2 13.5523 2 13V6.99999C2 6.44771 2.44772 5.99999 3 5.99999H4ZM3 6.99999H12V13H3V6.99999Z" fill="${bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`
  },
  // custom fields
  select: (props) =>
    `<svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.5 3C1.22386 3 1 3.22386 1 3.5C1 3.77614 1.22386 4 1.5 4H13.5C13.7761 4 14 3.77614 14 3.5C14 3.22386 13.7761 3 13.5 3H1.5ZM1 7.5C1 7.22386 1.22386 7 1.5 7H13.5C13.7761 7 14 7.22386 14 7.5C14 7.77614 13.7761 8 13.5 8H1.5C1.22386 8 1 7.77614 1 7.5ZM1 11.5C1 11.2239 1.22386 11 1.5 11H13.5C13.7761 11 14 11.2239 14 11.5C14 11.7761 13.7761 12 13.5 12H1.5C1.22386 12 1 11.7761 1 11.5Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  file: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 2.5C3 2.22386 3.22386 2 3.5 2H9.08579C9.21839 2 9.34557 2.05268 9.43934 2.14645L11.8536 4.56066C11.9473 4.65443 12 4.78161 12 4.91421V12.5C12 12.7761 11.7761 13 11.5 13H3.5C3.22386 13 3 12.7761 3 12.5V2.5ZM3.5 1C2.67157 1 2 1.67157 2 2.5V12.5C2 13.3284 2.67157 14 3.5 14H11.5C12.3284 14 13 13.3284 13 12.5V4.91421C13 4.51639 12.842 4.13486 12.5607 3.85355L10.1464 1.43934C9.86514 1.15804 9.48361 1 9.08579 1H3.5ZM4.5 4C4.22386 4 4 4.22386 4 4.5C4 4.77614 4.22386 5 4.5 5H7.5C7.77614 5 8 4.77614 8 4.5C8 4.22386 7.77614 4 7.5 4H4.5ZM4.5 7C4.22386 7 4 7.22386 4 7.5C4 7.77614 4.22386 8 4.5 8H10.5C10.7761 8 11 7.77614 11 7.5C11 7.22386 10.7761 7 10.5 7H4.5ZM4.5 10C4.22386 10 4 10.2239 4 10.5C4 10.7761 4.22386 11 4.5 11H10.5C10.7761 11 11 10.7761 11 10.5C11 10.2239 10.7761 10 10.5 10H4.5Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  radio: (props) =>
    `<svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.49985 0.877045C3.84216 0.877045 0.877014 3.84219 0.877014 7.49988C0.877014 11.1575 3.84216 14.1227 7.49985 14.1227C11.1575 14.1227 14.1227 11.1575 14.1227 7.49988C14.1227 3.84219 11.1575 0.877045 7.49985 0.877045ZM1.82701 7.49988C1.82701 4.36686 4.36683 1.82704 7.49985 1.82704C10.6328 1.82704 13.1727 4.36686 13.1727 7.49988C13.1727 10.6329 10.6328 13.1727 7.49985 13.1727C4.36683 13.1727 1.82701 10.6329 1.82701 7.49988ZM7.49999 9.49999C8.60456 9.49999 9.49999 8.60456 9.49999 7.49999C9.49999 6.39542 8.60456 5.49999 7.49999 5.49999C6.39542 5.49999 5.49999 6.39542 5.49999 7.49999C5.49999 8.60456 6.39542 9.49999 7.49999 9.49999Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  email: (props) =>
    `<svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 2C0.447715 2 0 2.44772 0 3V12C0 12.5523 0.447715 13 1 13H14C14.5523 13 15 12.5523 15 12V3C15 2.44772 14.5523 2 14 2H1ZM1 3L14 3V3.92494C13.9174 3.92486 13.8338 3.94751 13.7589 3.99505L7.5 7.96703L1.24112 3.99505C1.16621 3.94751 1.0826 3.92486 1 3.92494V3ZM1 4.90797V12H14V4.90797L7.74112 8.87995C7.59394 8.97335 7.40606 8.97335 7.25888 8.87995L1 4.90797Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  text: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.94993 2.95002L3.94993 4.49998C3.94993 4.74851 3.74845 4.94998 3.49993 4.94998C3.2514 4.94998 3.04993 4.74851 3.04993 4.49998V2.50004C3.04993 2.45246 3.05731 2.40661 3.07099 2.36357C3.12878 2.18175 3.29897 2.05002 3.49993 2.05002H11.4999C11.6553 2.05002 11.7922 2.12872 11.8731 2.24842C11.9216 2.32024 11.9499 2.40682 11.9499 2.50002L11.9499 2.50004V4.49998C11.9499 4.74851 11.7485 4.94998 11.4999 4.94998C11.2514 4.94998 11.0499 4.74851 11.0499 4.49998V2.95002H8.04993V12.05H9.25428C9.50281 12.05 9.70428 12.2515 9.70428 12.5C9.70428 12.7486 9.50281 12.95 9.25428 12.95H5.75428C5.50575 12.95 5.30428 12.7486 5.30428 12.5C5.30428 12.2515 5.50575 12.05 5.75428 12.05H6.94993V2.95002H3.94993Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  textarea: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.5 1C6.22386 1 6 1.22386 6 1.5C6 1.77614 6.22386 2 6.5 2C7.12671 2 7.45718 2.20028 7.65563 2.47812C7.8781 2.78957 8 3.28837 8 4V11C8 11.7116 7.8781 12.2104 7.65563 12.5219C7.45718 12.7997 7.12671 13 6.5 13C6.22386 13 6 13.2239 6 13.5C6 13.7761 6.22386 14 6.5 14C7.37329 14 8.04282 13.7003 8.46937 13.1031C8.47976 13.0886 8.48997 13.0739 8.5 13.0591C8.51003 13.0739 8.52024 13.0886 8.53063 13.1031C8.95718 13.7003 9.62671 14 10.5 14C10.7761 14 11 13.7761 11 13.5C11 13.2239 10.7761 13 10.5 13C9.87329 13 9.54282 12.7997 9.34437 12.5219C9.1219 12.2104 9 11.7116 9 11V4C9 3.28837 9.1219 2.78957 9.34437 2.47812C9.54282 2.20028 9.87329 2 10.5 2C10.7761 2 11 1.77614 11 1.5C11 1.22386 10.7761 1 10.5 1C9.62671 1 8.95718 1.29972 8.53063 1.89688C8.52024 1.91143 8.51003 1.92611 8.5 1.9409C8.48997 1.92611 8.47976 1.91143 8.46937 1.89688C8.04282 1.29972 7.37329 1 6.5 1ZM14 5H11V4H14C14.5523 4 15 4.44772 15 5V10C15 10.5523 14.5523 11 14 11H11V10H14V5ZM6 4V5H1L1 10H6V11H1C0.447715 11 0 10.5523 0 10V5C0 4.44772 0.447715 4 1 4H6Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  number: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 1.5C11 1.22386 10.7761 1 10.5 1C10.2239 1 10 1.22386 10 1.5V4H5V1.5C5 1.22386 4.77614 1 4.5 1C4.22386 1 4 1.22386 4 1.5V4H1.5C1.22386 4 1 4.22386 1 4.5C1 4.77614 1.22386 5 1.5 5H4V10H1.5C1.22386 10 1 10.2239 1 10.5C1 10.7761 1.22386 11 1.5 11H4V13.5C4 13.7761 4.22386 14 4.5 14C4.77614 14 5 13.7761 5 13.5V11H10V13.5C10 13.7761 10.2239 14 10.5 14C10.7761 14 11 13.7761 11 13.5V11H13.5C13.7761 11 14 10.7761 14 10.5C14 10.2239 13.7761 10 13.5 10H11V5H13.5C13.7761 5 14 4.77614 14 4.5C14 4.22386 13.7761 4 13.5 4H11V1.5ZM10 10V5H5V10H10Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  checkbox: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 3H12V12H3L3 3ZM2 3C2 2.44771 2.44772 2 3 2H12C12.5523 2 13 2.44772 13 3V12C13 12.5523 12.5523 13 12 13H3C2.44771 13 2 12.5523 2 12V3ZM10.3498 5.51105C10.506 5.28337 10.4481 4.97212 10.2204 4.81587C9.99275 4.65961 9.6815 4.71751 9.52525 4.94519L6.64048 9.14857L5.19733 7.40889C5.02102 7.19635 4.7058 7.16699 4.49327 7.34329C4.28073 7.5196 4.25137 7.83482 4.42767 8.04735L6.2934 10.2964C6.39348 10.4171 6.54437 10.4838 6.70097 10.4767C6.85757 10.4695 7.00177 10.3894 7.09047 10.2601L10.3498 5.51105Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  date: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 1C4.77614 1 5 1.22386 5 1.5V2H10V1.5C10 1.22386 10.2239 1 10.5 1C10.7761 1 11 1.22386 11 1.5V2H12.5C13.3284 2 14 2.67157 14 3.5V12.5C14 13.3284 13.3284 14 12.5 14H2.5C1.67157 14 1 13.3284 1 12.5V3.5C1 2.67157 1.67157 2 2.5 2H4V1.5C4 1.22386 4.22386 1 4.5 1ZM10 3V3.5C10 3.77614 10.2239 4 10.5 4C10.7761 4 11 3.77614 11 3.5V3H12.5C12.7761 3 13 3.22386 13 3.5V5H2V3.5C2 3.22386 2.22386 3 2.5 3H4V3.5C4 3.77614 4.22386 4 4.5 4C4.77614 4 5 3.77614 5 3.5V3H10ZM2 6V12.5C2 12.7761 2.22386 13 2.5 13H12.5C12.7761 13 13 12.7761 13 12.5V6H2ZM7 7.5C7 7.22386 7.22386 7 7.5 7C7.77614 7 8 7.22386 8 7.5C8 7.77614 7.77614 8 7.5 8C7.22386 8 7 7.77614 7 7.5ZM9.5 7C9.22386 7 9 7.22386 9 7.5C9 7.77614 9.22386 8 9.5 8C9.77614 8 10 7.77614 10 7.5C10 7.22386 9.77614 7 9.5 7ZM11 7.5C11 7.22386 11.2239 7 11.5 7C11.7761 7 12 7.22386 12 7.5C12 7.77614 11.7761 8 11.5 8C11.2239 8 11 7.77614 11 7.5ZM11.5 9C11.2239 9 11 9.22386 11 9.5C11 9.77614 11.2239 10 11.5 10C11.7761 10 12 9.77614 12 9.5C12 9.22386 11.7761 9 11.5 9ZM9 9.5C9 9.22386 9.22386 9 9.5 9C9.77614 9 10 9.22386 10 9.5C10 9.77614 9.77614 10 9.5 10C9.22386 10 9 9.77614 9 9.5ZM7.5 9C7.22386 9 7 9.22386 7 9.5C7 9.77614 7.22386 10 7.5 10C7.77614 10 8 9.77614 8 9.5C8 9.22386 7.77614 9 7.5 9ZM5 9.5C5 9.22386 5.22386 9 5.5 9C5.77614 9 6 9.22386 6 9.5C6 9.77614 5.77614 10 5.5 10C5.22386 10 5 9.77614 5 9.5ZM3.5 9C3.22386 9 3 9.22386 3 9.5C3 9.77614 3.22386 10 3.5 10C3.77614 10 4 9.77614 4 9.5C4 9.22386 3.77614 9 3.5 9ZM3 11.5C3 11.2239 3.22386 11 3.5 11C3.77614 11 4 11.2239 4 11.5C4 11.7761 3.77614 12 3.5 12C3.22386 12 3 11.7761 3 11.5ZM5.5 11C5.22386 11 5 11.2239 5 11.5C5 11.7761 5.22386 12 5.5 12C5.77614 12 6 11.7761 6 11.5C6 11.2239 5.77614 11 5.5 11ZM7 11.5C7 11.2239 7.22386 11 7.5 11C7.77614 11 8 11.2239 8 11.5C8 11.7761 7.77614 12 7.5 12C7.22386 12 7 11.7761 7 11.5ZM9.5 11C9.22386 11 9 11.2239 9 11.5C9 11.7761 9.22386 12 9.5 12C9.77614 12 10 11.7761 10 11.5C10 11.2239 9.77614 11 9.5 11Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  datetime: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 1C4.77614 1 5 1.22386 5 1.5V2H10V1.5C10 1.22386 10.2239 1 10.5 1C10.7761 1 11 1.22386 11 1.5V2H12.5C13.3284 2 14 2.67157 14 3.5V12.5C14 13.3284 13.3284 14 12.5 14H2.5C1.67157 14 1 13.3284 1 12.5V3.5C1 2.67157 1.67157 2 2.5 2H4V1.5C4 1.22386 4.22386 1 4.5 1ZM10 3V3.5C10 3.77614 10.2239 4 10.5 4C10.7761 4 11 3.77614 11 3.5V3H12.5C12.7761 3 13 3.22386 13 3.5V5H2V3.5C2 3.22386 2.22386 3 2.5 3H4V3.5C4 3.77614 4.22386 4 4.5 4C4.77614 4 5 3.77614 5 3.5V3H10ZM2 6V12.5C2 12.7761 2.22386 13 2.5 13H12.5C12.7761 13 13 12.7761 13 12.5V6H2ZM7 7.5C7 7.22386 7.22386 7 7.5 7C7.77614 7 8 7.22386 8 7.5C8 7.77614 7.77614 8 7.5 8C7.22386 8 7 7.77614 7 7.5ZM9.5 7C9.22386 7 9 7.22386 9 7.5C9 7.77614 9.22386 8 9.5 8C9.77614 8 10 7.77614 10 7.5C10 7.22386 9.77614 7 9.5 7ZM11 7.5C11 7.22386 11.2239 7 11.5 7C11.7761 7 12 7.22386 12 7.5C12 7.77614 11.7761 8 11.5 8C11.2239 8 11 7.77614 11 7.5ZM11.5 9C11.2239 9 11 9.22386 11 9.5C11 9.77614 11.2239 10 11.5 10C11.7761 10 12 9.77614 12 9.5C12 9.22386 11.7761 9 11.5 9ZM9 9.5C9 9.22386 9.22386 9 9.5 9C9.77614 9 10 9.22386 10 9.5C10 9.77614 9.77614 10 9.5 10C9.22386 10 9 9.77614 9 9.5ZM7.5 9C7.22386 9 7 9.22386 7 9.5C7 9.77614 7.22386 10 7.5 10C7.77614 10 8 9.77614 8 9.5C8 9.22386 7.77614 9 7.5 9ZM5 9.5C5 9.22386 5.22386 9 5.5 9C5.77614 9 6 9.22386 6 9.5C6 9.77614 5.77614 10 5.5 10C5.22386 10 5 9.77614 5 9.5ZM3.5 9C3.22386 9 3 9.22386 3 9.5C3 9.77614 3.22386 10 3.5 10C3.77614 10 4 9.77614 4 9.5C4 9.22386 3.77614 9 3.5 9ZM3 11.5C3 11.2239 3.22386 11 3.5 11C3.77614 11 4 11.2239 4 11.5C4 11.7761 3.77614 12 3.5 12C3.22386 12 3 11.7761 3 11.5ZM5.5 11C5.22386 11 5 11.2239 5 11.5C5 11.7761 5.22386 12 5.5 12C5.77614 12 6 11.7761 6 11.5C6 11.2239 5.77614 11 5.5 11ZM7 11.5C7 11.2239 7.22386 11 7.5 11C7.77614 11 8 11.2239 8 11.5C8 11.7761 7.77614 12 7.5 12C7.22386 12 7 11.7761 7 11.5ZM9.5 11C9.22386 11 9 11.2239 9 11.5C9 11.7761 9.22386 12 9.5 12C9.77614 12 10 11.7761 10 11.5C10 11.2239 9.77614 11 9.5 11Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
  hidden: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.3536 2.35355C13.5488 2.15829 13.5488 1.84171 13.3536 1.64645C13.1583 1.45118 12.8417 1.45118 12.6464 1.64645L10.6828 3.61012C9.70652 3.21671 8.63759 3 7.5 3C4.30786 3 1.65639 4.70638 0.0760002 7.23501C-0.0253338 7.39715 -0.0253334 7.60288 0.0760014 7.76501C0.902945 9.08812 2.02314 10.1861 3.36061 10.9323L1.64645 12.6464C1.45118 12.8417 1.45118 13.1583 1.64645 13.3536C1.84171 13.5488 2.15829 13.5488 2.35355 13.3536L4.31723 11.3899C5.29348 11.7833 6.36241 12 7.5 12C10.6921 12 13.3436 10.2936 14.924 7.76501C15.0253 7.60288 15.0253 7.39715 14.924 7.23501C14.0971 5.9119 12.9769 4.81391 11.6394 4.06771L13.3536 2.35355ZM9.90428 4.38861C9.15332 4.1361 8.34759 4 7.5 4C4.80285 4 2.52952 5.37816 1.09622 7.50001C1.87284 8.6497 2.89609 9.58106 4.09974 10.1931L9.90428 4.38861ZM5.09572 10.6114L10.9003 4.80685C12.1039 5.41894 13.1272 6.35031 13.9038 7.50001C12.4705 9.62183 10.1971 11 7.5 11C6.65241 11 5.84668 10.8639 5.09572 10.6114Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,

  // these are not header icons, but we load it into the library sprite map to render them inside the table cells
  external: (props) =>
    `<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 2C2.44772 2 2 2.44772 2 3V12C2 12.5523 2.44772 13 3 13H12C12.5523 13 13 12.5523 13 12V8.5C13 8.22386 12.7761 8 12.5 8C12.2239 8 12 8.22386 12 8.5V12H3V3L6.5 3C6.77614 3 7 2.77614 7 2.5C7 2.22386 6.77614 2 6.5 2H3ZM12.8536 2.14645C12.9015 2.19439 12.9377 2.24964 12.9621 2.30861C12.9861 2.36669 12.9996 2.4303 13 2.497L13 2.5V2.50049V5.5C13 5.77614 12.7761 6 12.5 6C12.2239 6 12 5.77614 12 5.5V3.70711L6.85355 8.85355C6.65829 9.04882 6.34171 9.04882 6.14645 8.85355C5.95118 8.65829 5.95118 8.34171 6.14645 8.14645L11.2929 3H9.5C9.22386 3 9 2.77614 9 2.5C9 2.22386 9.22386 2 9.5 2H12.4999H12.5C12.5678 2 12.6324 2.01349 12.6914 2.03794C12.7504 2.06234 12.8056 2.09851 12.8536 2.14645Z" fill="${props.bgColor}" fill-rule="evenodd" clip-rule="evenodd"></path></svg>`,
}
